/**
 * `<ActivityBanner>` — sticky transparency banner above the chat
 * surface (Crucible #602 umbrella, #668 Issue B, #669 Issue C, #688
 * Issue B2 — shared pin-store migration).
 * Source: `docs/c2-transparency-banner-design.md` §3 (visual spec),
 * §4 (state machine), §5 (click-to-stick pin override), §9
 * (animation perf + container/view split), §10 (skeleton);
 * `docs/c3-agents-panel-design.md` §3.1 (shared pin store) and §3.2
 * (shared `resolveSelectedRow`).
 *
 * The click-to-stick pin (C2 design §5; behaviour preserved post-B2
 * migration to the shared store at `state/pin-store.ts`):
 *   - Click the banner body → pin the currently resolved agent for
 *     `PIN_DURATION_MS` (10s).
 *   - Click again while pinned → release immediately.
 *   - 10s elapses without further interaction → pin clears via a
 *     single one-shot `setTimeout` owned by the shared store.
 *   - Pin survives `currentSelection` changes from the registry —
 *     the shared `resolveSelectedRow` reads `byAgentId[pin.agentId]`
 *     first.
 *   - Pin to an agentId no longer in `byAgentId` falls through
 *     silently; the heuristic resumes. The banner clears the
 *     dangling pin via `useSetPin(...)(null)` so subsequent renders
 *     reflect the heuristic-picked row in `aria-pressed`.
 *
 * Pin lifetime owners post-B2:
 *   - 10s expiry timer       — shared store (`pin-store.ts`).
 *   - Cross-`gameId` teardown — banner-local `useEffect` cleanup
 *                               calling `clearPin(gameId)`.
 *   - Cross-`sessionId` drop — banner-local `useEffect` (the store
 *                               carries pins across sessions until a
 *                               consumer drops them; the banner
 *                               drops on session roll to match C2's
 *                               behaviour).
 *   - Pinned-agent-vanished — banner-local `useEffect` calls
 *                               `setPin(null)`.
 *
 * Architectural shape (Codex round-1 HIGH on the original
 * draft — `React.memo` cannot suppress re-renders caused by an
 * internal subscription hook):
 *
 *   - Container `<ActivityBanner>` hosts `useActivitySnapshot`,
 *     `useFeatureFlags`, and subscribes to the shared pin store
 *     via `usePin(gameId)`. Re-renders on every snapshot push or
 *     pin transition. Computes the derived view props
 *     (`selectedRow`, `oldestWait`, ...) via pure helpers.
 *
 *   - Memoised `<ActivityBannerView>` receives the derived
 *     props. Its comparator audits the pinned row (when pinned)
 *     and the wait descriptor `(kind, reason, openedAt)` — not
 *     just `currentSelection` id.
 *
 *   - Leaf `<ElapsedTickSpan>` owns the 1Hz `useElapsedTick` so
 *     the per-second re-render is isolated to the elapsed slot
 *     and never bubbles to the view.
 *
 *   - Leaf `<WaitAnnouncer>` is a visually-hidden polite live
 *     region that announces ONLY when the oldest wait's
 *     `(kind, reason, openedAt)` identity changes. Routine
 *     heartbeat ticks stay landmark-only.
 *
 * Flag-gating (#671 centre-column shell is the canonical
 * mount-gate; this is defence-in-depth): the container null-
 * renders when `!flags.loaded || !flags.agentTransparency` so
 * any future caller that mounts the banner directly is still
 * gated. The shared `usePin` / `useSetPin` hooks do NOT gate on
 * the flag (correct — they are pure primitives); the banner is
 * the consumer that gates.
 *
 * The done-state 30s linger lives in `main/activity-registry.ts`
 * (`LINGER_MS = 30_000`, PR #695). The banner does NOT
 * implement a local done-hide timer — it renders `done` for
 * the duration of the registry's linger and unmounts on the
 * registry's null push.
 */

import React, { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Pin as PinIcon } from 'lucide-react';
import type {
  ActivityHeartbeatState,
  AgentActivity,
  AgentStreamChunk,
  WaitDescriptor,
} from '@volley/crucible-ipc-types/ipc-channels';
import type { AgentRow, MainAgentRow } from '../chat/state';
import { useActivitySnapshot } from '../hooks/useActivitySnapshot';
import { useElapsedTick } from '../hooks/useElapsedTick';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import type { WorkspaceRunState } from '../workspace/run-state';
import {
  PIN_DURATION_MS as STORE_PIN_DURATION_MS,
  clearPin,
  usePin,
  useSetPin,
} from '../state/pin-store';
import { MAIN_AGENT_ID, resolveSelectedRow, selectTranscriptBuffer } from '../state/selection';
import MiniTranscript from './MiniTranscript';

// ---------------------------------------------------------------------------
// Microcopy fallback constants (design §3 — exported for pinned-table tests).
// ---------------------------------------------------------------------------

/** Fallback subsystem string when the snapshot has no `subsystem` yet
 *  (e.g. orchestrator just started, no annotation has landed). */
export const SUBSYSTEM_FALLBACK = '(Starting up)';

/** Fallback action string for the same window. */
export const ACTION_FALLBACK = 'Reading project context';

/** Fixed copy for the `waiting` state — the action slot is the
 *  wait's `reason` string (verbatim from the gate author). */
export const WAITING_SUBSYSTEM = 'Waiting on your answer';

/** Crucible #793 — suffix appended to the banner's subsystem text when
 *  the resolved row's `stale: true`. Single-source mirrors
 *  `AgentRowCard.STALE_SUFFIX` so banner + row-card stay consistent
 *  (banner used to hide staleness entirely, leaving the user staring at
 *  `(Starting up) — Reading project context` for 10+ minutes during
 *  long phases — see #793 walkthrough). Kept as its own constant so a
 *  future copy tweak doesn't have to thread through both modules. */
export const STALE_SUFFIX = ' (No update 90s)';

/** Crucible #793 — action copy when the resolved row is running but
 *  has flipped `stale: true` (>90s without a fresh chunk). Mirrors
 *  `AgentRowCard.STALE_ACTION_COPY`. The banner previously kept showing
 *  the LAST annotation's action (or `ACTION_FALLBACK` if no annotation
 *  ever landed) — that froze copy is what made the user think the
 *  orchestrator was stuck. This copy is text-bearing, not colour-only,
 *  so screen-reader users get the same signal as sighted users. */
export const STALE_ACTION_COPY = 'Still working — no update in 90s';

// ---------------------------------------------------------------------------
// Pure helpers — co-located with the component so the tests can
// exercise them in isolation.
//
// Note: `resolveSelectedRow` lived locally in C2 (#669) and was lifted
// to `state/selection.ts` in B1 (#687). Pin shape lived locally as
// `ActivityBannerPin` and was lifted to `state/pin-store.ts` as `Pin`
// in the same B1 cut. The banner now imports both.
// ---------------------------------------------------------------------------

/**
 * Pick the oldest (smallest `openedAt`) wait from the registry's
 * `outstandingWaits` map. Returns `null` when the map is empty.
 * Banner renders only the oldest — multi-wait stacking is a panel
 * concern (design §6).
 */
export function pickOldestWait(waits: Record<string, WaitDescriptor>): WaitDescriptor | null {
  let oldest: WaitDescriptor | null = null;
  for (const w of Object.values(waits)) {
    if (oldest === null || w.openedAt < oldest.openedAt) {
      oldest = w;
    }
  }
  return oldest;
}

/**
 * Format the elapsed time as `12s` / `4m 12s` — mirrors the panel's
 * `formatDuration` shape so the two surfaces look consistent. Reads
 * `Date.now()` at call time when `stoppedAt` is null; otherwise
 * uses the terminal timestamp so the display stops counting.
 */
export function formatElapsed(
  startedAt: number,
  stoppedAt: number | null,
  now: number = Date.now(),
): string {
  const end = stoppedAt ?? now;
  const ms = Math.max(0, end - startedAt);
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remSec = seconds % 60;
  return remSec === 0 ? `${minutes}m` : `${minutes}m ${remSec}s`;
}

// ---------------------------------------------------------------------------
// Leaf: <ElapsedTickSpan>
// ---------------------------------------------------------------------------

/**
 * Renders the elapsed-time span and owns the 1Hz `useElapsedTick`
 * hook locally. Per design §9 Rule 3, isolating the tick to a leaf
 * is mandatory — if the parent view hosted the tick, the 1Hz re-
 * render would defeat the `React.memo` budget.
 *
 * `stopped` is true for terminal rows (status !== 'running'). When
 * stopped, the hook is a no-op (no interval), and `formatElapsed`
 * is anchored at `stoppedAt` so the value freezes.
 */
function ElapsedTickSpan({
  startedAt,
  stoppedAt,
}: {
  startedAt: number;
  stoppedAt: number | null;
}): React.JSX.Element {
  const isRunning = stoppedAt === null;
  // The tick value itself is unused — we read `Date.now()` via
  // `formatElapsed` at render time. The hook's job is only to
  // bump a state value so React re-renders this leaf.
  const tick = useElapsedTick(isRunning);
  void tick;
  return (
    <span className="text-xs tabular-nums text-white/50" data-testid="activity-banner-elapsed">
      {formatElapsed(startedAt, stoppedAt)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Leaf: <WaitAnnouncer>
// ---------------------------------------------------------------------------

/**
 * Visually-hidden polite live region that announces ONLY when the
 * oldest wait's `(kind, reason, openedAt)` identity changes. Per
 * design §8, routine state changes (active ↔ silent-but-active,
 * heartbeat ticks, subsystem mutations) stay landmark-only — the
 * signal-to-noise on full polite-live-region announcements would
 * be unusable.
 *
 * The component renders a permanent `<div role="status">` and
 * mutates its text content only on identity change. The `useRef`
 * memoises the previous identity key across renders.
 */
function WaitAnnouncer({ wait }: { wait: WaitDescriptor | null }): React.JSX.Element {
  const lastKey = useRef<string | null>(null);
  const key = wait ? `${wait.kind}|${wait.reason}|${wait.openedAt}` : null;
  const changed = key !== lastKey.current;

  // Persist the new key for next render. We do this in a layout-
  // free effect so the announce path can be exercised in tests.
  useEffect(() => {
    lastKey.current = key;
  }, [key]);

  // Render the reason only when the identity flipped on THIS
  // render — subsequent re-renders with the same wait will see
  // `changed === false` and emit an empty string. The polite
  // live region announces only when the rendered text changes,
  // so the empty-string interim keeps screen readers quiet.
  const text = changed && wait ? `Waiting on your answer: ${wait.reason}` : '';

  // Wrap the text in a keyed span so identity changes that produce
  // the same rendered text (kind-only or openedAt-only mutations
  // where `reason` is unchanged) still force a DOM mutation cycle
  // — React unmounts the old keyed span and mounts a new one, and
  // the polite live region observes the content change. Without
  // this, a direct A→B transition with same `reason` and no
  // intermediate same-identity render would skip the announcement
  // because the text string is identical. The empty-key sentinel
  // keeps the no-wait case stable.
  return (
    <div
      role="status"
      aria-live="polite"
      className="sr-only"
      data-testid="activity-banner-wait-announcer"
    >
      <span key={key ?? 'empty'}>{text}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// View: <ActivityBannerView>
// ---------------------------------------------------------------------------

interface ActivityBannerViewProps {
  heartbeatState: ActivityHeartbeatState;
  currentPhase: number | null;
  selectedRow: AgentActivity | null;
  oldestWait: WaitDescriptor | null;
  runState?: WorkspaceRunState | null;
  isPinned: boolean;
  onPinToggle: () => void;
  /**
   * Drawer-open state (C4 Issue F — banner verbose-mode drawer, C3
   * §4 Contract B). Owned by the container; the view renders the
   * chevron and the drawer body off this flag. The container also
   * extends the comparator below to audit this slot so chevron
   * clicks correctly trigger a re-render without breaking the
   * existing memoisation gains (C2 §9 Rule 2 — design note).
   */
  isDrawerOpen: boolean;
  /**
   * AgentId currently surfaced in the drawer. Routes
   * `selectTranscriptBuffer(...)` to the right chat-reducer slice.
   * Defaults to the snapshot's `currentSelection` (the row the
   * banner is showing) the first time the drawer is opened.
   */
  drawerAgentId: string;
  /**
   * Toggle handler — wired to the chevron's `onClick`. Identity
   * stable across snapshot pushes via `useCallback` in the
   * container so the comparator's `onDrawerToggle` reference
   * comparison stays cheap.
   */
  onDrawerToggle: () => void;
  /**
   * Dropdown change handler — wired to the in-drawer agent
   * selector. Stable across snapshot pushes; switches the
   * displayed agentId without closing the drawer.
   */
  onDrawerAgentChange: (agentId: string) => void;
  /**
   * Recent chunk buffer for the drawer's currently selected
   * agentId. Derived in the container via
   * `selectTranscriptBuffer(drawerAgentId, chatAgents,
   * chatMainAgent)` so the comparator stays cheap — the view
   * compares only the slice we actually render, not the full
   * `chatAgents` map.
   */
  drawerChunks: readonly AgentStreamChunk[];
  /** Monotonic counter — drives `MiniTranscript`'s stable per-chunk
   *  keys that survive front-trim shifts. */
  drawerChunksSeenTotal: number;
  /**
   * Ordered list of `agentId`s available in the drawer's dropdown.
   * Enumerated from `snapshot.byAgentId` in the container so the
   * dropdown only ever lists agents the wire currently knows
   * about; vanishing rows fall out automatically on the next push.
   */
  drawerAgentIds: readonly string[];
  /**
   * Stable id wired to the chevron's `aria-controls` and the
   * drawer panel's `id` attribute. Container generates via
   * `useId()` so SSR + reconciliation pick the same value.
   */
  drawerPanelId: string;
}

/**
 * Heartbeat dot base classes. The dot is 2.5×2.5px instead of the
 * pre-#787 2×2 — Issue #787 item 3 asks for a more prominent ring
 * size on the banner dot so a glancer sees the heartbeat without
 * leaning in. Ring + animation deltas per state live in
 * `HEARTBEAT_DOT_BY_STATE` below.
 */
const HEARTBEAT_DOT_BASE = 'inline-block h-2.5 w-2.5 flex-none rounded-full';

/**
 * Per-state colour + ring tokens for the heartbeat dot. Both
 * `active` and `silent-but-active` carry their own animation class
 * (Crucible #788) so the three states are visually distinct without
 * relying on the same pulsing-ring class on both. Reduced-motion
 * fallbacks in `globals.css` swap each animation for a distinct
 * static halo so motion-disabled users still get a delta.
 */
const HEARTBEAT_DOT_BY_STATE: Record<ActivityHeartbeatState, string> = {
  active: 'bg-cyan-400 activity-banner-heartbeat-active',
  'silent-but-active': 'bg-cyan-300 activity-banner-heartbeat-pulsing',
  waiting: 'bg-amber-400 ring-1 ring-amber-400/50',
  done: 'bg-white/30',
};

/**
 * Microcopy subsystem per state, against the resolved row. Returns
 * `{ text, isFallback }` so the view can attach the animated-
 * ellipsis class to fallback copy only (Crucible #787 item 5 —
 * `(Starting up)` cycles trailing dots while the real subsystem
 * text renders statically).
 *
 * Crucible #793 — when the row is running but `stale: true`
 * (registry's 90s watchdog flipped it), append `STALE_SUFFIX` to the
 * base subsystem text. The `(stale)` marker MUST be text-bearing
 * because the banner's heartbeat dot is the only other staleness
 * signal and dot colour is inaccessible on its own. Suffix applies
 * only on `running` status — `waiting` / `done` are intentionally
 * idle.
 */
function subsystemCopy(
  state: ActivityHeartbeatState,
  row: AgentActivity | null,
): { text: string; isFallback: boolean } {
  if (state === 'waiting') {
    return { text: WAITING_SUBSYSTEM, isFallback: false };
  }
  const stale = row?.stale === true && row.status === 'running';
  if (row?.subsystem !== null && row?.subsystem !== undefined) {
    return {
      text: stale ? `${row.subsystem}${STALE_SUFFIX}` : row.subsystem,
      isFallback: false,
    };
  }
  return {
    text: stale ? `${SUBSYSTEM_FALLBACK}${STALE_SUFFIX}` : SUBSYSTEM_FALLBACK,
    isFallback: true,
  };
}

/**
 * Microcopy action per state, against the resolved row + wait.
 * Returns `{ text, isFallback }` so the view can attach the
 * animated-ellipsis class to fallback copy only.
 *
 * Crucible #793 — staleness has priority over the cached
 * `row.action` value once the watchdog flips. Override only applies
 * when the heartbeat state would otherwise show the normal action
 * text; `waiting` / `done` keep their dedicated copy.
 */
function actionCopy(
  state: ActivityHeartbeatState,
  row: AgentActivity | null,
  wait: WaitDescriptor | null,
  currentPhase: number | null,
): { text: string; isFallback: boolean } {
  if (state === 'waiting') {
    return { text: wait?.reason ?? '', isFallback: false };
  }
  if (state === 'done' && row?.action === null && currentPhase !== null) {
    // Design §3 microcopy: done-state with no terminal action
    // copy falls back to "Phase N complete".
    return { text: `Phase ${currentPhase} complete`, isFallback: false };
  }
  if (state !== 'done' && row?.stale === true && row.status === 'running') {
    return { text: STALE_ACTION_COPY, isFallback: false };
  }
  if (row?.action !== null && row?.action !== undefined) {
    return { text: row.action, isFallback: false };
  }
  return { text: ACTION_FALLBACK, isFallback: true };
}

function ActivityBannerViewImpl({
  heartbeatState,
  currentPhase,
  selectedRow,
  oldestWait,
  runState,
  isPinned,
  onPinToggle,
  isDrawerOpen,
  drawerAgentId,
  onDrawerToggle,
  onDrawerAgentChange,
  drawerChunks,
  drawerChunksSeenTotal,
  drawerAgentIds,
  drawerPanelId,
}: ActivityBannerViewProps): React.JSX.Element {
  const dotClass = `${HEARTBEAT_DOT_BASE} ${HEARTBEAT_DOT_BY_STATE[heartbeatState]}`;
  const subsystem = runState
    ? { text: runState.label, isFallback: false }
    : subsystemCopy(heartbeatState, selectedRow);
  const action = runState
    ? { text: runState.detail ?? '', isFallback: false }
    : actionCopy(heartbeatState, selectedRow, oldestWait, currentPhase);
  const isDone = heartbeatState === 'done';

  // Crucible #787 item 5: animated-ellipsis suffix on fallback
  // microcopy only (e.g. "(Starting up)" / "Reading project
  // context"). The trailing dots cycle via a CSS pseudo-element;
  // real subsystem / action text renders without the suffix.
  //
  // Bugbot round-2 LOW: suppress in `done` state — the banner
  // visually communicates completion there (50% opacity, static
  // dot) and animated loading dots contradict the "done"
  // signal. Same suppression applies to `waiting` because the
  // wait announcer is the load-bearing surface in that state and
  // its copy is already non-fallback.
  const showAnimatedEllipsis = !isDone && heartbeatState !== 'waiting';
  const subsystemAnimatedClass =
    showAnimatedEllipsis && subsystem.isFallback ? ' animated-ellipsis' : '';
  const actionAnimatedClass = showAnimatedEllipsis && action.isFallback ? ' animated-ellipsis' : '';

  // The elapsed clock is anchored on the resolved row's
  // `startedAt`. When the row is terminal, `stoppedAt` is used
  // so the value freezes; otherwise it ticks via the leaf hook.
  // No row → no elapsed slot.
  const elapsedSlot = selectedRow ? (
    <ElapsedTickSpan startedAt={selectedRow.startedAt} stoppedAt={selectedRow.stoppedAt} />
  ) : null;

  return (
    <>
      <section
        role="region"
        aria-label="Live agent activity"
        data-testid="activity-banner"
        data-heartbeat-state={heartbeatState}
        data-wait-kind={oldestWait?.kind ?? ''}
        className={`@container/banner flex h-12 w-full items-center gap-3 border-b border-white/5 bg-white/[0.04] px-4 py-2 backdrop-blur-sm transition-opacity duration-200 ${
          isDone ? 'opacity-50' : 'opacity-100'
        }`}
      >
        <span className={dotClass} aria-hidden="true" data-testid="activity-banner-dot" />
        <button
          type="button"
          onClick={onPinToggle}
          aria-pressed={isPinned}
          aria-label="Pin banner to this agent"
          data-testid="activity-banner-body"
          className="flex min-w-0 flex-1 items-center gap-2 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400/60"
        >
          {isPinned ? (
            <PinIcon
              className="h-3 w-3 flex-none text-white/50"
              aria-hidden="true"
              data-testid="activity-banner-pin-icon"
            />
          ) : null}
          <span
            className={`truncate text-sm font-medium text-white/90 transition-opacity duration-200${subsystemAnimatedClass}`}
            data-testid="activity-banner-subsystem"
            data-fallback={subsystem.isFallback ? 'true' : 'false'}
          >
            {subsystem.text}
          </span>
          <span
            className="@max-[519px]/banner:hidden truncate text-sm text-white/40"
            aria-hidden="true"
            data-testid="activity-banner-em-dash"
          >
            —
          </span>
          <span
            className={`@max-[519px]/banner:hidden truncate text-sm font-normal text-white/65 transition-opacity duration-200${actionAnimatedClass}`}
            data-testid="activity-banner-action"
            data-fallback={action.isFallback ? 'true' : 'false'}
          >
            {action.text}
          </span>
        </button>
        <div
          className="@max-[379px]/banner:hidden flex flex-none items-center gap-2"
          data-testid="activity-banner-meta"
        >
          {elapsedSlot}
          {currentPhase !== null ? (
            <>
              <span className="@max-[719px]/banner:hidden text-white/30" aria-hidden="true">
                ·
              </span>
              <span
                className="@max-[719px]/banner:hidden text-xs text-white/40"
                data-testid="activity-banner-phase"
              >
                Phase {currentPhase}
              </span>
            </>
          ) : null}
        </div>
        {/* Details chevron — C4 Issue F (C3 §4 Contract B). The
                    chevron lives in the memoised view so the click +
                    `aria-expanded` flip participates in the same
                    re-render pass as every other view prop change.
                    Drawer-open state itself is owned by the container
                    (`isDrawerOpen` is a derived view prop, not a
                    local `useState` here) so the comparator can audit
                    it. The chevron carries `aria-controls={drawerPanelId}`
                    so SR users following the relationship land on
                    the drawer panel below the banner row. */}
        <button
          type="button"
          onClick={onDrawerToggle}
          aria-expanded={isDrawerOpen}
          aria-controls={drawerPanelId}
          aria-label={isDrawerOpen ? 'Hide agent details drawer' : 'Show agent details drawer'}
          data-testid="activity-banner-drawer-toggle"
          className="flex-none text-[11px] text-white/50 hover:text-white/80 motion-safe:active:scale-[0.97] motion-safe:transition-transform motion-safe:duration-75 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400/60"
        >
          {isDrawerOpen ? '▴ Hide details' : '▾ Details'}
        </button>
      </section>
      {isDrawerOpen ? (
        <div
          id={drawerPanelId}
          role="region"
          aria-label="Agent details drawer"
          data-testid="activity-banner-drawer"
          className="w-full border-b border-white/5 bg-white/[0.02]"
        >
          {/* Dropdown selector at the drawer's top-left
                        (design Contract B). Lists all agentIds the
                        snapshot currently exposes; defaults to the
                        currentSelection on first open via the
                        container's lazy initialiser. */}
          <div className="flex items-center gap-2 px-4 py-2">
            <label
              htmlFor={`${drawerPanelId}-agent-select`}
              className="text-[11px] uppercase tracking-wide text-white/40"
            >
              Agent
            </label>
            <select
              id={`${drawerPanelId}-agent-select`}
              value={drawerAgentId}
              onChange={(e) => onDrawerAgentChange(e.target.value)}
              data-testid="activity-banner-drawer-agent-select"
              className="rounded border border-white/10 bg-black/30 px-2 py-0.5 text-xs text-white/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400/60"
            >
              {drawerAgentIds.map((id) => (
                <option key={id} value={id}>
                  {id === MAIN_AGENT_ID ? 'Orchestrator' : id}
                </option>
              ))}
            </select>
          </div>
          {/* The transcript itself — shared C3 component
                        (extracted in #699). The drawer is the only
                        C4 consumer of `MiniTranscript`; C3's
                        per-row expand also mounts it but with
                        chunks routed by the row's own agentId. */}
          <MiniTranscript chunks={drawerChunks} chunksSeenTotal={drawerChunksSeenTotal} />
        </div>
      ) : null}
    </>
  );
}

/**
 * Memo comparator (design §9 Rule 2). Returns `true` when the
 * banner can SKIP a re-render.
 *
 * Audits:
 *   - `heartbeatState` (active / silent-but-active / waiting / done)
 *   - `currentPhase` (banner shows it as suffix when >=720px)
 *   - `isPinned` (Pin icon visibility + aria-pressed)
 *   - `selectedRow` identity by `(agentId, subsystem, action,
 *     startedAt, stoppedAt, stale, status)` — covers pinned-row
 *     updates, NOT just `currentSelection` id (Codex round-1 HIGH).
 *     `stale` + `status` were added in Crucible #793 so the
 *     90s-watchdog timer push can re-render the banner with the
 *     `(stale)` suffix even when subsystem/action/timestamps are
 *     unchanged.
 *   - `oldestWait` identity by `(kind, reason, openedAt)` —
 *     covers reason-only mutations (Codex round-1 HIGH).
 *   - `onPinToggle` reference — wrapped in `useCallback` upstream,
 *     so this only flips when the underlying snapshot's
 *     `currentSelection` changes.
 *   - C4 Issue F drawer slots:
 *       - `isDrawerOpen` — chevron `aria-expanded` + drawer
 *         panel mount/unmount.
 *       - `drawerAgentId` — drives the dropdown's selected value
 *         AND the transcript-buffer derivation upstream (so the
 *         buffer's `(chunks, chunksSeenTotal)` references also
 *         flip on a drawer-agent change).
 *       - `drawerChunks` / `drawerChunksSeenTotal` — chunk feed
 *         to `MiniTranscript`. Reference equality is the
 *         load-bearing check: the chat reducer either appends a
 *         new chunk (new array reference) or doesn't.
 *       - `drawerAgentIds` — populates the dropdown <option>
 *         list. Stable across heartbeat ticks because the
 *         container memoises the array.
 *       - `drawerPanelId` — stable across re-renders (useId);
 *         we still compare so a defensive id regeneration would
 *         trip a re-render rather than silently desync
 *         `aria-controls` from the panel's `id`.
 *       - `onDrawerToggle` / `onDrawerAgentChange` — stable
 *         `useCallback` refs from the container.
 */
function viewPropsEqual(prev: ActivityBannerViewProps, next: ActivityBannerViewProps): boolean {
  if (prev.heartbeatState !== next.heartbeatState) return false;
  if (prev.currentPhase !== next.currentPhase) return false;
  if (prev.runState !== next.runState) return false;
  if (prev.isPinned !== next.isPinned) return false;
  if (prev.onPinToggle !== next.onPinToggle) return false;

  // Selected row — `null`/non-null mismatch first, then per-field
  // identity. We compare the cheap discriminators that the view
  // actually renders.
  const pr = prev.selectedRow;
  const nr = next.selectedRow;
  if (pr === null && nr !== null) return false;
  if (pr !== null && nr === null) return false;
  if (pr !== null && nr !== null) {
    if (pr.agentId !== nr.agentId) return false;
    if (pr.subsystem !== nr.subsystem) return false;
    if (pr.action !== nr.action) return false;
    if (pr.startedAt !== nr.startedAt) return false;
    if (pr.stoppedAt !== nr.stoppedAt) return false;
    // Crucible #793 — `stale` flips on a timer push from the
    // registry (90s watchdog) without any change to subsystem /
    // action / startedAt / stoppedAt. Without this audit, the
    // banner would memo-skip the push and never re-render to
    // surface the "(stale)" suffix + "Still working — no update
    // in 90s" copy. `status` is also added because the staleness
    // helpers gate on `row.status === 'running'` (defence in
    // depth — `status` could in principle flip from `running` to
    // a terminal value without `stoppedAt` mutating in the same
    // tick).
    if (pr.stale !== nr.stale) return false;
    if (pr.status !== nr.status) return false;
  }

  // Oldest wait — `(kind, reason, openedAt)` identity, not just
  // map-key. A registry that mutates `reason` in place is still
  // caught.
  const pw = prev.oldestWait;
  const nw = next.oldestWait;
  if (pw === null && nw !== null) return false;
  if (pw !== null && nw === null) return false;
  if (pw !== null && nw !== null) {
    if (pw.kind !== nw.kind) return false;
    if (pw.reason !== nw.reason) return false;
    if (pw.openedAt !== nw.openedAt) return false;
  }

  // Drawer slots — every slot is read by the view, so each one
  // must be audited. Reference equality on the chunk array and
  // the agentId list is intentional: the chat reducer + container
  // both produce a NEW array on mutation, so a stable reference
  // means "no change rendered". An accidentally re-built array
  // upstream would correctly re-render here.
  //
  // Codex round-1 P2: when the drawer is closed, `MiniTranscript`
  // is not mounted, so the chunk-buffer slots can mutate without
  // any visible effect. Comparing `drawerChunks` /
  // `drawerChunksSeenTotal` while closed would force the view to
  // re-render on every streamed chunk that lands during a build —
  // the chat reducer allocates a new `recentChunks` array per
  // chunk, so reference equality fails every time. Skip those two
  // slots while closed (the open-vs-closed transition itself is
  // already audited by the `isDrawerOpen` check above, and the
  // open-side comparison catches any chunk drift the moment the
  // drawer is opened).
  if (prev.isDrawerOpen !== next.isDrawerOpen) return false;
  if (prev.drawerAgentId !== next.drawerAgentId) return false;
  if (next.isDrawerOpen) {
    if (prev.drawerChunks !== next.drawerChunks) return false;
    if (prev.drawerChunksSeenTotal !== next.drawerChunksSeenTotal) {
      return false;
    }
  }
  if (prev.drawerAgentIds !== next.drawerAgentIds) return false;
  if (prev.drawerPanelId !== next.drawerPanelId) return false;
  if (prev.onDrawerToggle !== next.onDrawerToggle) return false;
  if (prev.onDrawerAgentChange !== next.onDrawerAgentChange) return false;

  return true;
}

export const ActivityBannerView = memo(ActivityBannerViewImpl, viewPropsEqual);
ActivityBannerView.displayName = 'ActivityBannerView';

// ---------------------------------------------------------------------------
// Container: <ActivityBanner>
// ---------------------------------------------------------------------------

export interface ActivityBannerProps {
  gameId: string;
  runState?: WorkspaceRunState | null;
  /**
   * Chat reducer's per-specialist slice — passed through ONLY for
   * the C4 verbose-mode drawer's `MiniTranscript` chunk buffer
   * (design `docs/c3-agents-panel-design.md` §4 Contract B + §2
   * "Two read paths"). Row STATE for the banner row itself still
   * comes from the snapshot wire via `resolveSelectedRow`; this
   * prop is read only by `selectTranscriptBuffer` when the drawer
   * is open.
   *
   * Optional: when omitted the drawer renders the "No activity
   * yet." empty state (the same shape the chat reducer's slice
   * produces during the turn-boundary drift window — design §2).
   * Workspace.tsx passes both props; downstream consumers that
   * haven't migrated yet keep working with the banner-row-only
   * rendering.
   */
  chatAgents?: ReadonlyMap<string, AgentRow>;
  /**
   * Chat reducer's orchestrator slice — same role as `chatAgents`
   * but routed through `selectTranscriptBuffer('__main__', ...)`.
   * Optional alongside `chatAgents` so the drawer renders an empty
   * transcript when callers haven't widened their prop bag yet.
   */
  chatMainAgent?: MainAgentRow | null;
}

/**
 * Frozen empty map for the `chatAgents` fallback when the caller
 * omits the prop. Stable reference so `selectTranscriptBuffer`
 * `.get(...)` always lands on the same EMPTY_CHUNKS sentinel and
 * the view comparator's reference-equality check holds across
 * snapshot pushes.
 */
const EMPTY_AGENTS: ReadonlyMap<string, AgentRow> = new Map();

/**
 * Frozen empty agentId list for the drawer dropdown's fallback
 * when no snapshot has arrived yet. Stable reference so the view
 * comparator's `drawerAgentIds` check short-circuits on the pre-
 * snapshot render.
 */
const EMPTY_AGENT_IDS: readonly string[] = Object.freeze([]);

/**
 * Pin duration in ms (design §5 / C3 §3.1). Re-exported from the
 * shared store so existing callers (`#669` pin-PR tests, downstream
 * consumers) keep the same import path — the store remains the
 * single source of truth.
 */
export const PIN_DURATION_MS = STORE_PIN_DURATION_MS;

export function ActivityBanner({
  gameId,
  runState,
  chatAgents,
  chatMainAgent,
}: ActivityBannerProps): React.JSX.Element | null {
  const flags = useFeatureFlags();
  const snapshot = useActivitySnapshot(gameId);

  // ---------------------------------------------------------------
  // C4 Issue F — drawer state (container side of C3 §4 Contract B).
  // Per C2 §9 Rule 2, drawer-open state lives in the container so
  // the memoised view receives it as a derived prop and the
  // comparator can audit it. The drawer panel + chevron themselves
  // live in the view so they participate in the same memoised
  // render pass as every other view prop change.
  // ---------------------------------------------------------------

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // `null` means "use whatever the snapshot's currentSelection
  // resolves to" — the dropdown defaults to the displayed row on
  // first open. The first explicit dropdown change replaces this
  // with a concrete agentId; from then on the drawer stays on
  // that agent even if the banner's heuristic-picked row drifts.
  const [drawerAgentIdOverride, setDrawerAgentIdOverride] = useState<string | null>(null);
  // Stable id wired to the chevron's `aria-controls` + the drawer
  // panel's `id`. `useId` is the React 19 idiom — survives SSR
  // hydration and stays stable across re-renders.
  const drawerPanelId = useId();
  // The shell the click-outside listener watches. Covers both the
  // banner row AND the drawer panel so a click anywhere INSIDE the
  // wrapped tree counts as "inside". Outside the wrapper → drawer
  // closes.
  const hostRef = useRef<HTMLDivElement>(null);

  // Pin state — sourced from the shared module-scoped store
  // (`state/pin-store.ts`, B1 / Crucible #687). The store owns the
  // 10s one-shot expiry timer and per-`gameId` isolation; the
  // banner still owns the cross-game cleanup, the cross-session
  // drop, and the pinned-agent-vanished drop because those are
  // consumer-level concerns the pure store deliberately doesn't
  // try to infer (see C3 design §3.1).
  const pin = usePin(gameId);
  const setPin = useSetPin(gameId, snapshot?.sessionId ?? null);

  // Cross-`gameId` teardown for the module-scoped store. A React
  // `key={gameId}` reset on the parent does NOT clear the store on
  // its own — see C3 design §3.1 Codex round-1 MED. The cleanup
  // runs on unmount AND on every `gameId` transition.
  useEffect(() => {
    return () => {
      clearPin(gameId);
    };
  }, [gameId]);

  // Drop the pin proactively when keeping it would mislead the
  // UI. The render-time guards (`resolveSelectedRow` filters on
  // `pin.sessionId`; `isPinned` requires `byAgentId` membership +
  // session match) eliminate any one-paint flicker on this render;
  // this effect simply settles store state for subsequent renders
  // so the pin shape matches what the view shows.
  //
  //   - Session roll (same game, different `sessionId`): the
  //     pin was captured for a session that no longer matches.
  //   - Pinned agent vanished (specialists cleared on a new
  //     turn): the fallback chain has already routed through
  //     `resolveSelectedRow`; clear the pin so a re-pin click
  //     can target the heuristic-picked row.
  //
  // We clear via `setPin(null)` (the store setter accepts `null`
  // to clear). State changes happen inside the effect — never in
  // render — so React's render-phase setState guard is not
  // tripped.
  useEffect(() => {
    if (snapshot === null) return;
    if (pin === null) return;
    if (pin.sessionId !== snapshot.sessionId) {
      setPin(null);
      return;
    }
    if (snapshot.byAgentId[pin.agentId] === undefined) {
      setPin(null);
    }
  }, [snapshot, pin, setPin]);

  // Capture the latest resolved agentId in a ref so the pin click
  // handler can reach it without re-creating the callback on every
  // snapshot push. The callback identity stays stable (good for the
  // memoised view's `onPinToggle` reference) while still pinning
  // the currently-displayed agent at click time. The session id is
  // captured by the store's `useSetPin` itself, so we only need
  // the agent id here.
  const selectedAgentIdRef = useRef<string | null>(null);
  // Mirror the store's live pin for the click handler — it needs
  // "is anything pinned right now?" without recreating the
  // callback on every pin change.
  const pinRef = useRef(pin);
  pinRef.current = pin;

  // The pin-toggle handler is stable across snapshot pushes so
  // the memoised view's `onPinToggle` reference doesn't churn.
  // It reads `selectedAgentIdRef.current` (refreshed below on
  // every render) instead of closing over the current selection.
  //
  // Behaviour parity with C2 banner-local `useState`: if ANY pin
  // is active, clear it immediately; otherwise pin the currently
  // resolved agent for `PIN_DURATION_MS`. We bypass the store's
  // own toggle-off (which only fires on a same-`agentId` re-click)
  // and call `setPin(null)` explicitly so that clicking the body
  // while pinned to a vanished/different agent still clears,
  // matching C2's "any pin → clear" semantics.
  const onPinToggle = useCallback(() => {
    if (pinRef.current !== null) {
      setPin(null);
      return;
    }
    const agentId = selectedAgentIdRef.current;
    if (agentId === null) return;
    setPin(agentId);
  }, [setPin]);

  // Drawer-toggle callback. Stable across snapshot pushes (no
  // closure over the snapshot) so the view's `onDrawerToggle`
  // reference doesn't churn — the comparator audits it.
  const onDrawerToggle = useCallback(() => {
    setIsDrawerOpen((prev) => !prev);
  }, []);

  // Dropdown-change callback. Replaces the `null` override (which
  // means "track the snapshot's currentSelection") with a concrete
  // agentId. Stable across renders.
  const onDrawerAgentChange = useCallback((agentId: string) => {
    setDrawerAgentIdOverride(agentId);
  }, []);

  // Whether the banner is visible on THIS render. Drives both the
  // click-outside listener mount-gate AND the drawer-reset effect
  // below — the banner null-renders without unmounting (the
  // shell holds it mounted across `templateCompatible` flips and
  // session-roll snapshot-null windows), so the drawer state and
  // its document listener need an explicit reset when visibility
  // drops to keep the listener from pinning a stale `hostRef`
  // (Codex round-1 P2).
  const isBannerVisible =
    flags.loaded && flags.agentTransparency && snapshot !== null && snapshot.templateCompatible;

  // Close the drawer + reset the dropdown override when the banner
  // becomes invisible. Without this the drawer-open `useState`
  // would survive a session-end / template-too-old transition,
  // and the next banner-mount cycle would briefly render an open
  // drawer pointing at stale state. Pairs with the listener gate
  // below so a no-longer-visible banner can't keep an outside-
  // click listener registered against a null `hostRef`.
  useEffect(() => {
    if (isBannerVisible) return;
    setIsDrawerOpen(false);
    setDrawerAgentIdOverride(null);
  }, [isBannerVisible]);

  // Click-outside listener — closes the drawer when the drawer is
  // open AND the user clicks somewhere outside the host wrapper.
  // The listener is mounted only while the drawer is open AND the
  // banner is visible (Codex round-1 P2 — without the visibility
  // gate, a banner that null-rendered between snapshot pushes
  // would leave a document `mousedown` listener pinned to a null
  // `hostRef`, and the listener's early-return on `host === null`
  // would prevent outside clicks from ever closing the drawer in
  // that state).
  //
  // `mousedown` is the right phase — running on `click` would
  // fire AFTER the chevron's own `onClick`, so a chevron click
  // while the drawer is open would close-then-reopen. `mousedown`
  // runs before the synthetic click reaches the chevron, but
  // because `hostRef.current.contains(target)` is `true` for
  // anything inside the host (including the chevron itself), the
  // listener correctly NO-OPs on chevron clicks and only fires
  // for truly-outside clicks.
  useEffect(() => {
    if (!isDrawerOpen) return;
    if (!isBannerVisible) return;
    const onDocMouseDown = (event: MouseEvent): void => {
      const host = hostRef.current;
      if (host === null) return;
      const target = event.target;
      if (target instanceof Node && host.contains(target)) return;
      setIsDrawerOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
    };
  }, [isDrawerOpen, isBannerVisible]);

  // Drawer-agent enumeration. `byAgentId` is a fresh object on
  // every snapshot push, so a naive `Object.keys(...)` would
  // produce a new array reference every push and defeat the view
  // comparator's `drawerAgentIds` reference-equality check.
  // `useMemo` keyed on `byAgentId` rebuilds the array only when a
  // push lands; the inner element-wise comparison against the
  // previous result (stashed in `drawerAgentIdsRef`) preserves the
  // array identity when the SET of agentIds is unchanged across
  // pushes — covering the heartbeat-tick case where the registry
  // re-emits the same agents map with mutated `lastChunkAt` slots.
  //
  // Hook lives BEFORE the early returns so the rules-of-hooks
  // order stays stable across render passes; when `snapshot` is
  // null the memo returns the frozen-empty sentinel (cheap) and
  // the result is unused (early return below).
  const byAgentId = snapshot?.byAgentId;
  const drawerAgentIdsRef = useRef<readonly string[]>(EMPTY_AGENT_IDS);
  const drawerAgentIds = useMemo<readonly string[]>(() => {
    if (byAgentId === undefined) return EMPTY_AGENT_IDS;
    const next = Object.keys(byAgentId).sort();
    const prev = drawerAgentIdsRef.current;
    if (prev.length === next.length) {
      let equal = true;
      for (let i = 0; i < prev.length; i++) {
        if (prev[i] !== next[i]) {
          equal = false;
          break;
        }
      }
      if (equal) return prev;
    }
    const frozen: readonly string[] = Object.freeze(next);
    drawerAgentIdsRef.current = frozen;
    return frozen;
  }, [byAgentId]);

  // Flag-gate (defence in depth — #671 is the canonical mount-
  // gate). Until the renderer has read the flag (`!flags.loaded`)
  // OR the flag is OFF (`!flags.agentTransparency`), the banner
  // null-renders. A stray push that arrives ahead of the flag
  // load still cannot mount a visible banner — and because the
  // click handler is unreachable in that state, no pin can be
  // set while the flag is off (belt-and-braces).
  if (!flags.loaded || !flags.agentTransparency) return null;

  // No snapshot → no build in flight for this game.
  if (snapshot === null) return null;

  // Template too old → the warning banner takes over (design §7
  // mutual exclusivity). The activity banner's microcopy would
  // be misleading because the registry skipped marker parsing.
  if (!snapshot.templateCompatible) return null;

  const selectedRow = resolveSelectedRow(snapshot, pin);
  const oldestWait = pickOldestWait(snapshot.outstandingWaits);
  const heartbeatState: ActivityHeartbeatState = runState
    ? runState.tone === 'waiting'
      ? 'waiting'
      : runState.tone === 'success'
        ? 'done'
        : runState.tone === 'neutral'
          ? snapshot.heartbeatState
          : 'active'
    : snapshot.heartbeatState;

  // Keep the agent-id ref in sync with the latest render so the
  // click handler picks up the currently-displayed agent. We
  // update on every render after the flag/snapshot/template
  // gates — that's the only window where the button is reachable,
  // so the ref is populated at click time.
  selectedAgentIdRef.current = selectedRow?.agentId ?? null;

  // `isPinned` is computed synchronously from the snapshot so the
  // view reflects the actual state on THIS render — not after the
  // pin-drop effect re-runs (Macroscope / Codex round-2). Three
  // conditions must all hold:
  //   - a pin exists;
  //   - the pin's `sessionId` matches the current session (so a
  //     pin from a previous session never resurrects on session
  //     B's first push, even if both sessions use `__main__`);
  //   - the pinned agentId is present in this push's `byAgentId`
  //     (so the heuristic-picked fallback row never carries
  //     `aria-pressed='true'`).
  const isPinned =
    pin !== null &&
    pin.sessionId === snapshot.sessionId &&
    snapshot.byAgentId[pin.agentId] !== undefined;

  // Resolve the drawer's effective agentId. When the dropdown
  // override is `null`, the drawer tracks whatever the banner is
  // displaying (the snapshot's heuristic-picked row). When the
  // user has explicitly switched to a specific agent, that
  // override wins — UNLESS the chosen agent has vanished from the
  // snapshot (specialists clear at turn boundaries), in which
  // case we fall back to the displayed row again so the dropdown
  // never points at an absent option. The empty-string fallback
  // is unreachable in practice (a non-null snapshot always
  // carries at least `__main__`) but keeps the type narrow.
  const fallbackDrawerAgentId = selectedRow?.agentId ?? snapshot.currentSelection ?? MAIN_AGENT_ID;
  const drawerAgentId =
    drawerAgentIdOverride !== null && snapshot.byAgentId[drawerAgentIdOverride] !== undefined
      ? drawerAgentIdOverride
      : fallbackDrawerAgentId;

  // Route the dropdown's selected agentId to its chunk buffer via
  // the shared `selectTranscriptBuffer` selector (C3 §9). The
  // selector resolves `MAIN_AGENT_ID` against the orchestrator
  // slice and every other id against the specialists map; missing
  // slices fall through to the frozen `EMPTY_CHUNKS` sentinel so
  // the view comparator's reference-equality check stays meaningful
  // during the turn-boundary drift window (design §2).
  const { chunks: drawerChunks, chunksSeenTotal: drawerChunksSeenTotal } = selectTranscriptBuffer(
    drawerAgentId,
    chatAgents ?? EMPTY_AGENTS,
    chatMainAgent ?? null,
  );

  return (
    <div ref={hostRef} data-testid="activity-banner-host">
      <ActivityBannerView
        heartbeatState={heartbeatState}
        currentPhase={snapshot.currentPhase}
        selectedRow={selectedRow}
        oldestWait={oldestWait}
        runState={runState}
        isPinned={isPinned}
        onPinToggle={onPinToggle}
        isDrawerOpen={isDrawerOpen}
        drawerAgentId={drawerAgentId}
        onDrawerToggle={onDrawerToggle}
        onDrawerAgentChange={onDrawerAgentChange}
        drawerChunks={drawerChunks}
        drawerChunksSeenTotal={drawerChunksSeenTotal}
        drawerAgentIds={drawerAgentIds}
        drawerPanelId={drawerPanelId}
      />
      {/* Polite live region lives outside the memoised view so it
                can correctly clear its text on same-identity re-renders
                (the announcer's `changed` check is one-shot per
                identity, and the memo would otherwise skip the clear).
                The announcer is a leaf with no other render state, so
                re-rendering it on every container push is free. */}
      <WaitAnnouncer wait={oldestWait} />
    </div>
  );
}
